import type { Dispatch, PayloadAction, TypedStartListening } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { sessionStorageGet, sessionStorageSet } from '~/utilities/webStorage'

import type { BackendControlledFullScreenCardDTO } from '@capture/client-api/src/orval'

import {
    FetchedAccountInfo,
    FetchedFullScreenCards,
    NewUserCreated,
    TOSTermsVerified,
} from '../currentUser/actions'
import type { getSunsetDeletionDate } from '../currentUser/selectors'
import { getIsReadOnlyUser } from '../currentUser/selectors'
import { requiredSubscription } from '../resume/resumeSlice'
import { PurchaseSucceeded, RefusedSubscription } from '../storagePlan/actions'

/**
 * Declare list of modals managed by the placement
 */
export enum ManagedModal {
    Subscription = 'Subscription',
    TermsOfService = 'TermsOfService',
    SunsetDeletion = 'SunsetDeletion',
}

export enum ModalWithOptions {
    FullScreenMessage = 'FullScreenMessage',
}

export type FullScreenModalWithOptions = {
    type: ModalWithOptions.FullScreenMessage
    options: BackendControlledFullScreenCardDTO
}

export type VisibleModal =
    | {
          type: ManagedModal
      }
    | FullScreenModalWithOptions

export type ModalManagerState = {
    visibleModals: VisibleModal[]
}

const initialState: ModalManagerState = {
    visibleModals: [],
}

const modalManagerSlice = createSlice({
    name: 'modalManager',
    initialState: initialState,
    reducers: {
        showModal: (state, action: PayloadAction<VisibleModal>) => {
            if (
                state.visibleModals.findIndex((modal) => modal.type === action.payload.type) === -1
            ) {
                state.visibleModals.push(action.payload)
            }
        },
        hideModal: (state) => {
            state.visibleModals.shift()
        },
        dismissSunsetDeletionModal: (state) => {
            if (state.visibleModals[0]?.type === ManagedModal.SunsetDeletion) {
                state.visibleModals.shift()
            }
        },
        dismissFullScreenCardModal: (state) => {
            if (state.visibleModals[0]?.type === ModalWithOptions.FullScreenMessage) {
                state.visibleModals.shift()
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(requiredSubscription, (state, _action) => {
                state.visibleModals.push({ type: ManagedModal.Subscription })
            })
            .addCase(TOSTermsVerified, (state, _action) => {
                if (state.visibleModals[0]?.type === ManagedModal.TermsOfService) {
                    state.visibleModals.shift()
                }
            })
            .addCase(RefusedSubscription, (state, _action) => {
                if (state.visibleModals[0]?.type === ManagedModal.Subscription) {
                    state.visibleModals.shift()
                }
            })
            .addCase(PurchaseSucceeded, (state, _action) => {
                if (state.visibleModals[0]?.type === ManagedModal.Subscription) {
                    state.visibleModals.shift()
                }
            })
            .addCase(FetchedFullScreenCards, (state, action) => {
                state.visibleModals.push(
                    ...action.payload.map((options) => {
                        return {
                            type: ModalWithOptions.FullScreenMessage,
                            options,
                        }
                    }),
                )
            })
    },
})

type ModalListenerState = StateWithModalManager & StateOfSelector<typeof getSunsetDeletionDate>
export const addModalListeners = (
    startListening: TypedStartListening<ModalListenerState, Dispatch>,
) => {
    startListening({
        actionCreator: dismissSunsetDeletionModal,
        effect: () => {
            sessionStorageSet('hasSeenSunsetDeletionPrompt', 'true')
        },
    })

    // When the account info is fetched, we check whether we have user communications to show
    startListening({
        actionCreator: FetchedAccountInfo,
        effect: (action, listenerApi) => {
            const accountInfo = action.payload

            // 1. check if we have TOS to approve
            if (accountInfo.tos_to_approve !== null) {
                listenerApi.dispatch(showModal({ type: ManagedModal.TermsOfService }))
            }

            // 2. check if we have a deletion notice to show
            const hasSeenSunsetDeletionPrompt =
                sessionStorageGet('hasSeenSunsetDeletionPrompt') !== null
            if (accountInfo.sunset_delete_date !== undefined && !hasSeenSunsetDeletionPrompt) {
                listenerApi.dispatch(showModal({ type: ManagedModal.SunsetDeletion }))
            }
        },
    })

    // when a new user is created, we check if they have a subscription
    startListening({
        actionCreator: NewUserCreated,
        effect: (_action, listenerApi) => {
            // Due to requiresLogin is wrapped in every logged in route, this would be triggered for every route changes for new user,
            // but we only want to handle this once
            listenerApi.unsubscribe()

            const currentState = listenerApi.getState()
            const isReadOnlyUser = getIsReadOnlyUser(currentState)

            if (isReadOnlyUser) {
                listenerApi.dispatch(showModal({ type: ManagedModal.Subscription }))
            }
        },
    })
}

export const { showModal, hideModal, dismissSunsetDeletionModal, dismissFullScreenCardModal } =
    modalManagerSlice.actions

export const modalManagerReducerMapObj = {
    [modalManagerSlice.name]: modalManagerSlice.reducer,
}

export type StateWithModalManager = StateOfReducerMapObj<typeof modalManagerReducerMapObj>
